import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { VariableSizeList } from 'react-window';
import { GroupBase, MenuListProps } from 'react-select';
import { AppLoader } from '@components/Core/AppLoader';
import { Obj } from '@/types/common';
import { MAX_ITEMS_SHOW, MAX_MENU_HEIGHT } from './constants';

const HEIGHT = 40;
/**
 * VariableSizeList from react-window—is used directly as a JSX element but is not compatible with
 *  React’s JSX component expectations, due to how it's typed or exported.
 * */
const ListComponent = VariableSizeList as any;

export function MenuList<T extends Obj>(
  props: MenuListProps<T, boolean, GroupBase<T>>,
  optionValue?: keyof T,
  isLoading?: boolean,
  maxMenuHeight?: number
): ReactElement {
  const { options, children, getValue } = props;
  const [value] = getValue();
  const rowHeights = useRef<any>({});
  const listRef = useRef<VariableSizeList<any>>(null);
  const [rerender, setRerender] = useState(false);

  let initialOffset = 0;
  if (value) {
    const indexSelected = options.findIndex((item) => {
      return (item as T)[optionValue || 'value'] === value[optionValue || 'value'];
    });
    initialOffset = (indexSelected !== -1 ? indexSelected - 1 : 0) * HEIGHT;
  }

  const getRowHeight = useCallback(
    (index: number): number => rowHeights.current[index] || HEIGHT,
    [rerender]
  );

  const setRowHeight = useCallback((index: number, size: number) => {
    if (listRef.current) listRef.current.resetAfterIndex(0);
    rowHeights.current = { ...rowHeights.current, [index]: size };
  }, []);

  const RenderRow = useCallback(
    ({ index, style }: { index: number; style: any }) => {
      const rowRef = useRef<HTMLDivElement | null>(null);

      useEffect(() => {
        if (rowRef.current) {
          setRowHeight(index, rowRef.current.firstElementChild?.clientHeight || HEIGHT);
        }
      }, [index, rowRef]);
      return children && Array.isArray(children) ? (
        <div ref={rowRef} style={style}>
          {children[index]}
        </div>
      ) : null;
    },
    [children]
  );

  const getMenuHeight = useMemo(() => {
    if (Object.values(rowHeights.current).length) {
      const sliceNum = Math.min(
        (children && Array.isArray(children) && children.length) || MAX_ITEMS_SHOW,
        MAX_ITEMS_SHOW
      );
      const rowsHeight: number = (Object.values(rowHeights.current) as number[])
        .slice(0, sliceNum)
        .reduce((a: number, b: number) => {
          return a + b;
        }, 0);
      return Math.min(Math.max(rowsHeight, HEIGHT), maxMenuHeight || MAX_MENU_HEIGHT);
    }
    return 120;
  }, [rowHeights.current, rerender, options, children]);

  if (isLoading)
    return (
      <div className="h-[120px] flex-centred px-[10px]">
        <AppLoader />
      </div>
    );

  return children && Array.isArray(children) && children.length > 0 ? (
    <ListComponent
      height={getMenuHeight}
      width="100%"
      itemCount={children.length}
      itemSize={getRowHeight}
      onItemsRendered={() => setRerender(!rerender)}
      initialScrollOffset={initialOffset}
      ref={listRef}
    >
      {RenderRow}
    </ListComponent>
  ) : (
    <div className="h-[120px] flex-centred px-[10px]">
      No items in list. Start typing to search items.
    </div>
  );
}
